// Reports step 2 (spec 044 D84–D88): the summaries buildReportStats writes every hour —
// stats/{YYYY-MM-DD} and stats/audience — turned into the Revenue chart, the cards' trend
// lines and the two audience cards; and stats/hours into Busiest hours (step 4, D95–D97); and
// the subject chart (D126–D129). Pure, so Jest covers it.
import { toDate } from "./ticketPresentation";
import { countText } from "./kitPresentation";
import { chipOf } from "./reportNumbers";
import { MONTH_NAME, MONTH_SHORT, cairoDateKey, cairoParts, reportWindows } from "./reportPeriods";

// D85: Today by the hour, 12 months by the month, the rest by the day.
export const chartStep = (key) => (key === "today" ? "hour" : key === "12m" ? "month" : "day");

// The Cairo days a period's charts read: its first day to today — every chart ends today.
export const statsSpan = (windows) => ({ from: cairoDateKey(windows.whole.from), to: cairoDateKey(windows.now) });

// "2026-09-29" → "2026-09-30": calendar days, no clock involved.
const dayAfter = (key) => {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
};
const pad = (n) => String(n).padStart(2, "0");

// The chart's points and each card's trend line. `days` maps "YYYY-MM-DD" to a summary; a day
// with no summary counts as nothing that day (the page checks the job has run at all). A
// point adds up its days (or, for Today, one hour of today's summary); active teachers count
// each tutor once however many of the point's days they taught on.
export const chartSeries = (days, windows) => {
  const step = chartStep(windows.key);
  const { from, to } = statsSpan(windows);
  const points = [];
  if (step === "hour") {
    const hours = (days.get(to) || {}).hours || [];
    for (let h = 0; h <= cairoParts(windows.now).h; h += 1) points.push({ label: `${pad(h)}:00`, parts: [hours[h] || {}] });
  } else {
    for (let k = from; k <= to; k = dayAfter(k)) {
      const [, m, d] = k.split("-").map(Number);
      const label = step === "month" ? MONTH_SHORT[m - 1] : `${d} ${MONTH_SHORT[m - 1]}`;
      const last = points[points.length - 1];
      if (step === "month" && last && last.label === label) last.parts.push(days.get(k) || {});
      else points.push({ label, parts: [days.get(k) || {}] });
    }
  }
  const sum = (parts, field) => parts.reduce((n, p) => n + (Number(p[field]) || 0), 0);
  return {
    step,
    labels: points.map((p) => p.label),
    revenue: points.map((p) => Math.round(sum(p.parts, "revenue") * 100) / 100),
    newStudents: points.map((p) => sum(p.parts, "newStudents")),
    sessions: points.map((p) => sum(p.parts, "sessions")),
    activeTeachers: points.map((p) => new Set(p.parts.flatMap((x) => x.teacherIds || [])).size),
  };
};

// Figma's seven gridlines from 0 (D126, "Income over time"): the smallest 1, 2 or 5 × 10ⁿ
// step whose sixth reaches the top bar.
export const axisTicks = (max) => {
  const target = Number.isFinite(max) && max > 0 ? max / 6 : 0;
  let power = 1;
  while (5 * power < target) power *= 10;
  const step = [1, 2, 5].map((k) => k * power).find((s) => s >= target);
  return Array.from({ length: 7 }, (_, i) => i * step);
};

// "updated 14:20" when the job last ran today, "updated 28 Sep, 14:20" before that — Cairo's
// clock; null when it never ran.
export const updatedLine = (builtAt, now) => {
  const d = toDate(builtAt);
  if (!d) return null;
  const p = cairoParts(d);
  const time = `${pad(p.h)}:${pad(p.mi)}`;
  return cairoDateKey(d) === cairoDateKey(now) ? `updated ${time}` : `updated ${p.d} ${MONTH_SHORT[p.m - 1]}, ${time}`;
};

const share = (n, of) => (of ? `${((n / of) * 100).toFixed(1)}%` : "—");
const plural = (n, one, many) => `${countText(n)} ${n === 1 ? one : many}`;

// D90, D92: the Users by country card — the top five (every country with `all`), each with its
// share of all accounts (the frame's "of total") and a bar against the top country's, and
// "Other N countries" for the rest. `byCode` is the map: a country's step on the five-blue
// key, on a log scale so a country with one account still shows, and its tooltip lines; a
// country with no users is `nobody`.
export const countryCard = (audience, all = false) => {
  const list = audience.countries || [];
  const accounts = audience.accounts || 0;
  const top = list.length ? list[0].accounts : 0;
  const shade = (n) => (top <= 1 ? 5 : 1 + Math.round((4 * Math.log(n)) / Math.log(top)));
  const rest = all ? [] : list.slice(5);
  const restN = rest.reduce((n, c) => n + c.accounts, 0);
  const noPhone = audience.noPhone ? ` · ${countText(audience.noPhone)} with no phone number` : "";
  return {
    subtitle: `${plural(accounts, "account", "accounts")} across ${plural(list.length, "country", "countries")}${noPhone}`,
    lead: list.length ? `${list[0].name} alone is ${share(top, accounts)} of all accounts` : null,
    rows: (all ? list : list.slice(0, 5)).map((c) => ({
      key: c.code,
      name: c.name,
      count: countText(c.accounts),
      share: share(c.accounts, accounts),
      fill: top ? c.accounts / top : 0,
    })),
    other: rest.length
      ? { label: `Other ${plural(rest.length, "country", "countries")}`, text: `${plural(restN, "account", "accounts")} · ${share(restN, accounts)}` }
      : null,
    more: list.length > 5,
    byCode: new Map(
      list.map((c) => [c.code, { shade: shade(c.accounts), users: plural(c.accounts, "user", "users"), share: `${share(c.accounts, accounts)} of total` }])
    ),
    nobody: { shade: 0, users: "0 users", share: `${share(0, accounts)} of total` },
  };
};

// D93: the Users by gender card — every student account in a ring by gender (Not given only
// when there is one), the accounts by number of children with each bar against the largest
// row, and who learns: the job's full run counts them, "—" until it has.
export const genderCard = (audience) => {
  const n = audience.accounts || 0;
  const g = audience.gender || {};
  const c = audience.children || {};
  const learners = audience.learners || null;
  const split = [
    { key: "male", label: "Male", count: g.male || 0, tone: "blue" },
    { key: "female", label: "Female", count: g.female || 0, tone: "violet" },
    { key: "notGiven", label: "Not given", count: g.notGiven || 0, tone: "track" },
  ]
    .filter((s) => s.key !== "notGiven" || s.count > 0)
    .map((s) => ({ ...s, text: countText(s.count), share: share(s.count, n), fraction: n ? s.count / n : 0 }));
  const rows = [
    ["none", "No children"],
    ["one", "1 child"],
    ["two", "2 children"],
    ["threePlus", "3+ children"],
  ].map(([key, label]) => ({ key, label, count: c[key] || 0 }));
  const most = Math.max(0, ...rows.map((r) => r.count));
  return {
    total: countText(n),
    unit: n === 1 ? "account" : "accounts",
    split,
    children: rows.map((r) => ({ ...r, text: countText(r.count), share: share(r.count, n), fill: most ? r.count / most : 0 })),
    learners: [
      { key: "adults", label: "Adult learners", note: "Same-gender teacher matching applies", tone: "blue" },
      { key: "children", label: "Child learners", note: "Either gender, with guardian consent", tone: "sky" },
    ].map((l) => ({ ...l, text: learners ? countText(learners[l.key] || 0) : "—" })),
  };
};

// 044 D126–D129: Lessons over time, by subject. Figma's three subjects, in its legend order and
// colours (figmaTokens chart.series). Any other name ("No subject", a renamed subject) is
// "Other subjects", grey, in the legend only when it has lessons in the 12 months (R77, R78).
export const SUBJECTS = [
  { name: "Quran", tone: "quran" },
  { name: "Arabic Language", tone: "arabic" },
  { name: "Islamic Studies", tone: "islamic" },
];
export const OTHER_SUBJECTS = "Other subjects";

// The month `by` months from Cairo's month `p`, as "2026-09": calendar months, no clock.
const monthFrom = (p, by) => new Date(Date.UTC(p.y, p.m - 1 + by, 1)).toISOString().slice(0, 7);
const monthEnd = (month) => {
  const [y, m] = month.split("-").map(Number);
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
};

// The Cairo days the subject chart reads (D127, D128): the 12 months before the chart's, then
// its 12, this one to its last day (buildReportStats writes the days ahead, R76).
export const subjectSpan = (now) => {
  const p = cairoParts(now);
  return { from: `${monthFrom(p, -23)}-01`, to: monthEnd(monthFrom(p, 0)) };
};

// `days` maps "YYYY-MM-DD" to a summary whose `subjects` counts its lessons that are not
// cancelled, by subject. The bars are the 12 months up to this one, oldest first, each with
// its total and one part per legend subject. The chip compares the 12 months with the 12
// before them, whole, as Sessions booked does (D78), and is null when those had none.
export const subjectChart = (days, now) => {
  const p = cairoParts(now);
  const months = Array.from({ length: 24 }, (_, i) => monthFrom(p, i - 23));
  const counts = new Map(months.map((m) => [m, {}]));
  const known = new Set(SUBJECTS.map((s) => s.name));
  (days || new Map()).forEach((d, id) => {
    const month = counts.get(String(id).slice(0, 7));
    if (!month || !d || !d.subjects) return;
    Object.entries(d.subjects).forEach(([name, n]) => {
      const key = known.has(name) ? name : OTHER_SUBJECTS;
      month[key] = (month[key] || 0) + (Number(n) || 0);
    });
  });
  const sumOf = (c) => Object.values(c).reduce((a, b) => a + b, 0);
  const shown = months.slice(12);
  const legend = shown.some((m) => counts.get(m)[OTHER_SUBJECTS])
    ? [...SUBJECTS, { name: OTHER_SUBJECTS, tone: "other" }]
    : SUBJECTS;
  const bars = shown.map((m, i) => {
    const [y, mm] = m.split("-").map(Number);
    const c = counts.get(m);
    return {
      key: m,
      short: MONTH_SHORT[mm - 1],
      full: `${MONTH_NAME[mm - 1]} ${y}`,
      current: i === 11,
      total: sumOf(c),
      parts: legend.map((s) => ({ name: s.name, tone: s.tone, count: c[s.name] || 0 })),
    };
  });
  const total = bars.reduce((n, b) => n + b.total, 0);
  const before = months.slice(0, 12).reduce((n, m) => n + sumOf(counts.get(m)), 0);
  return {
    bars,
    legend,
    total,
    ticks: axisTicks(Math.max(0, ...bars.map((b) => b.total))),
    chip: chipOf(total, before, reportWindows("12m", now).whole.prev),
  };
};

export const lessonsText = (n) => plural(n, "lesson", "lessons");

// A bar for a screen reader: "September 2026: 6 lessons (Quran 4, Arabic Language 2), booked so far".
export const barLabel = (bar) => {
  const parts = bar.parts.filter((x) => x.count).map((x) => `${x.name} ${countText(x.count)}`);
  return `${bar.full}: ${lessonsText(bar.total)}${parts.length ? ` (${parts.join(", ")})` : ""}${bar.current ? ", booked so far" : ""}`;
};

// The line under the chart's title (D128).
export const subjectSubtitle = (chart, updated) =>
  [chart && chart.chip ? "By subject, against the 12 months before" : "By subject", updated].filter(Boolean).join(" · ");

// Busiest hours (D95–D97): stats/hours — 14 Cairo days × 24 Cairo hours of the tutors who offer
// an hour (`offered`) and those booked in it (`booked`) — as the grid, the four boxes and the
// footer. `view` is "booked" (a square is its booked tutors) or "open" (its tutors still free).
const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const dayName = (key) => {
  const [y, m, d] = key.split("-").map(Number);
  return `${WEEKDAY_SHORT[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]} ${d} ${MONTH_SHORT[m - 1]}`;
};
const hourName = (h) => `${h % 12 || 12}${h % 24 < 12 ? "am" : "pm"}`;
const hourSpan = (from, to) => `${hourName(from)} – ${hourName(to)}`;
const percent = (n, of) => `${Math.round((n / of) * 100)}%`;

export const hoursCard = (hours, view, now) => {
  const days = (hours && hours.days) || [];
  const open = view === "open";
  const offset = new Intl.DateTimeFormat("en-GB", { timeZone: "Africa/Cairo", timeZoneName: "shortOffset" })
    .formatToParts(now)
    .find((p) => p.type === "timeZoneName").value;
  const byHour = Array.from({ length: 24 }, (_, h) => ({
    offered: days.reduce((n, d) => n + d.offered[h], 0),
    booked: days.reduce((n, d) => n + d.booked[h], 0),
  }));
  const offeredAll = byHour.reduce((n, x) => n + x.offered, 0);
  const bookedAll = byHour.reduce((n, x) => n + x.booked, 0);
  const full = (d, h) => d.offered[h] > 0 && d.booked[h] === d.offered[h];
  const valueOf = (d, h) => (open ? d.offered[h] - d.booked[h] : d.booked[h]);
  const top = Math.max(0, ...days.flatMap((d) => d.offered.map((_, h) => valueOf(d, h))));

  // D96, over the 14 days; ties go to the earlier hour or day.
  const hoursWith = byHour.map((x, h) => h).filter((h) => byHour[h].offered > 0);
  const bookedShare = (h) => byHour[h].booked / byHour[h].offered;
  const peak = hoursWith.reduce((a, h) => (a === null || bookedShare(h) > bookedShare(a) ? h : a), null);
  const freest = hoursWith.reduce((a, h) => (a === null || bookedShare(h) < bookedShare(a) ? h : a), null);
  const freePct = (h) => (byHour[h].offered ? Math.round((1 - bookedShare(h)) * 100) : null);
  let from = freest;
  let to = freest;
  if (freest !== null) {
    while (from > 0 && freePct(from - 1) === freePct(freest)) from -= 1;
    while (to < 23 && freePct(to + 1) === freePct(freest)) to += 1;
  }
  const busiest = days.reduce((a, d) => (d.lessons > (a ? a.lessons : 0) ? d : a), null);
  const fullCount = days.reduce((n, d) => n + d.offered.filter((_, h) => full(d, h)).length, 0);
  const none = { value: "—", caption: "" };

  return {
    subtitle: `${open ? "Open slots" : "Booked sessions"} per hour across all teachers · next 14 days · Cairo time (${offset})`,
    peakHour: peak,
    stats: [
      { key: "peak", label: "Peak hour", ...(peak === null ? none : { value: hourSpan(peak, peak + 1), caption: `${percent(byHour[peak].booked, byHour[peak].offered)} of slots booked` }) },
      { key: "day", label: "Busiest day", ...(busiest ? { value: dayName(busiest.day), caption: `${plural(busiest.lessons, "session", "sessions")} booked` } : none) },
      { key: "full", label: "Fully booked hours", value: countText(fullCount), caption: "no teacher left free" },
      { key: "open", label: "Most open", ...(freest === null ? none : { value: hourSpan(from, to + 1), caption: `${freePct(freest)}% of slots still free` }) },
    ],
    hourLabels: Array.from({ length: 24 }, (_, h) => (h % 3 === 0 ? hourName(h) : "")),
    rows: days.map((d, i) => ({
      key: d.day,
      label: dayName(d.day),
      today: d.day === cairoDateKey(now),
      gap: i === 7,
      cells: d.offered.map((offered, h) => {
        const booked = d.booked[h];
        const who = offered === 1 ? "teacher" : "teachers";
        return {
          key: h,
          level: Math.min(valueOf(d, h), 5),
          full: !open && full(d, h),
          tip: {
            title: dayName(d.day),
            value: hourSpan(h, h + 1),
            note: !offered
              ? "No teacher offers this hour"
              : open
                ? `${countText(offered - booked)} of ${countText(offered)} ${who} free`
                : `${countText(booked)} of ${countText(offered)} ${who} booked`,
          },
        };
      }),
    })),
    legend: {
      title: open ? "Open slots per hour" : "Sessions per hour",
      keys: [0, 1, 2, 3, 4, 5].map((level) => ({
        level,
        label: level < 5 ? String(level) : top > 5 ? `5–${top}` : top === 5 ? "5" : "5+",
      })),
      full: !open,
    },
    summary: !offeredAll
      ? "No teacher hours in the next 14 days"
      : open
        ? `${countText(offeredAll - bookedAll)} of ${countText(offeredAll)} hour slots open · ${percent(offeredAll - bookedAll, offeredAll)}`
        : `${countText(bookedAll)} of ${countText(offeredAll)} hour slots booked · ${percent(bookedAll, offeredAll)}`,
  };
};
